import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ChefStatusControl = ({ bookingId, booking }) => {
  const [currentStatus, setCurrentStatus] = useState('not_started');
  const [location, setLocation] = useState(null);
  const [storeName, setStoreName] = useState('');
  const [notes, setNotes] = useState('');
  const [tracking, setTracking] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (bookingId) {
      loadCurrentStatus();
    }
  }, [bookingId]);

  // Auto-track location when en route
  useEffect(() => {
    let interval;
    if (tracking && currentStatus === 'en_route') {
      getCurrentLocation();
      interval = setInterval(getCurrentLocation, 30000); // Every 30 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [tracking, currentStatus]);

  const loadCurrentStatus = async () => {
    try {
      const response = await axios.get(`/tracking/journey/${bookingId}`);
      setCurrentStatus(response.data.current_status || 'not_started');
    } catch (error) {
      console.error('Failed to load status:', error);
    }
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          };
          setLocation(loc);
          
          // Send location update if en route
          if (currentStatus === 'en_route') {
            sendLocationUpdate(loc);
          }
        },
        (error) => {
          console.error('Error getting location:', error);
        }
      );
    }
  };

  const sendLocationUpdate = async (loc) => {
    try {
      await axios.post('/tracking/location', {
        booking_id: bookingId,
        latitude: loc.latitude,
        longitude: loc.longitude,
        accuracy: loc.accuracy
      });
    } catch (error) {
      console.error('Failed to send location update:', error);
    }
  };

  const updateStatus = async (newStatus) => {
    setUpdating(true);
    
    // Get current location
    await new Promise((resolve) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setLocation({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude
            });
            resolve();
          },
          () => resolve()
        );
      } else {
        resolve();
      }
    });

    try {
      await axios.post('/tracking/status', {
        booking_id: bookingId,
        status: newStatus,
        latitude: location?.latitude,
        longitude: location?.longitude,
        store_name: storeName || undefined,
        notes: notes || undefined
      });

      setCurrentStatus(newStatus);
      setStoreName('');
      setNotes('');
      
      // Start tracking when en route
      if (newStatus === 'en_route') {
        setTracking(true);
      } else if (newStatus === 'arrived') {
        setTracking(false);
      }
      
      alert('Status updated successfully! Customer has been notified.');
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Failed to update status. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const statusButtons = [
    { status: 'going_to_store', label: '🚗 Going to Store', color: 'blue', enabled: ['not_started'] },
    { status: 'at_store', label: '🛒 At Store Shopping', color: 'purple', enabled: ['going_to_store'] },
    { status: 'leaving_store', label: '📦 Leaving Store', color: 'yellow', enabled: ['at_store'] },
    { status: 'en_route', label: '🚙 En Route to Customer', color: 'orange', enabled: ['leaving_store'] },
    { status: 'arrived', label: '✅ Arrived at Customer', color: 'green', enabled: ['en_route'] }
  ];

  const currentButton = statusButtons.find(b => b.status === currentStatus);
  const nextButtons = statusButtons.filter(b => b.enabled.includes(currentStatus));

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
        <span className="text-3xl">📍</span>
        Journey Status Control
      </h2>

      {/* Current Status Display */}
      <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg p-4 mb-6 border-2 border-orange-200">
        <p className="text-sm text-gray-600 font-medium mb-1">Current Status</p>
        <div className="flex items-center gap-3">
          <span className="text-3xl">{currentButton?.label.split(' ')[0]}</span>
          <p className="text-xl font-bold text-gray-900">
            {currentButton?.label.substring(2) || 'Not Started'}
          </p>
        </div>
        {tracking && (
          <p className="text-sm text-green-600 font-semibold mt-2 flex items-center gap-2">
            <span className="inline-block w-2 h-2 bg-green-600 rounded-full animate-pulse"></span>
            GPS tracking active
          </p>
        )}
      </div>

      {/* Status Update Buttons */}
      {nextButtons.length > 0 && (
        <div className="space-y-4 mb-6">
          <p className="text-sm font-semibold text-gray-700">Update to:</p>
          {nextButtons.map((button) => (
            <div key={button.status}>
              <button
                onClick={() => {
                  // If going to store, ask for store name
                  if (button.status === 'at_store') {
                    const name = prompt('Enter store name (optional):');
                    if (name !== null) {
                      setStoreName(name);
                      updateStatus(button.status);
                    }
                  } else {
                    updateStatus(button.status);
                  }
                }}
                disabled={updating}
                className={`w-full bg-${button.color}-600 hover:bg-${button.color}-700 disabled:bg-gray-400 text-white px-6 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-lg`}
              >
                {updating ? 'Updating...' : button.label}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Location Info */}
      {location && (
        <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
          <p className="font-semibold mb-1">📍 Current Location</p>
          <p>Lat: {location.latitude.toFixed(6)}</p>
          <p>Lon: {location.longitude.toFixed(6)}</p>
        </div>
      )}

      {/* Info Box */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-900">
          <strong>ℹ️ How it works:</strong>
        </p>
        <ul className="text-sm text-blue-800 mt-2 space-y-1 ml-4">
          <li>• Update your status as you progress through the journey</li>
          <li>• Customer will receive real-time notifications</li>
          <li>• GPS tracking activates automatically when en route</li>
          <li>• Location updates every 30 seconds while traveling</li>
        </ul>
      </div>
    </div>
  );
};

export default ChefStatusControl;
